/**
 * Failure model for the console data layer.
 *
 * The contract's error surface is RFC 9457 `application/problem+json` with a
 * stable `code`, so "the server said problem+json" is a first-class case and
 * everything else is explicitly NOT — a transport failure or a non-conforming
 * body must never be dressed up as a problem document.
 */

import type { Problem, ProblemCode } from './types';

export type ApiErrorKind =
  /** Server returned a conforming `application/problem+json` body. */
  | 'problem'
  /** Network/timeout: no response at all. */
  | 'transport'
  /** Response arrived but is not valid for the contract. */
  | 'contract'
  /** The console asked for data this client cannot fetch yet — no endpoint publishes
   *  it, or the live door exists and the provider method is not wired to it. */
  | 'pending-endpoint';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly problem?: Problem;
  readonly status?: number;
  /** `Retry-After` (seconds) — only the 429 surface carries it. */
  readonly retryAfterSeconds?: number;
  /** Which console capability asked for data this client cannot serve yet. */
  readonly capability?: string;
  /** Raw body excerpt for `contract` errors (never trusted, only displayed). */
  readonly rawBody?: string;

  constructor(
    kind: ApiErrorKind,
    message: string,
    init: {
      problem?: Problem;
      status?: number;
      retryAfterSeconds?: number;
      capability?: string;
      rawBody?: string;
      cause?: unknown;
    } = {},
  ) {
    super(message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.kind = kind;
    if (init.problem !== undefined) this.problem = init.problem;
    if (init.status !== undefined) this.status = init.status;
    if (init.retryAfterSeconds !== undefined) {
      this.retryAfterSeconds = init.retryAfterSeconds;
    }
    if (init.capability !== undefined) this.capability = init.capability;
    if (init.rawBody !== undefined) this.rawBody = init.rawBody;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** The problem document behind a failure, if there is one. */
export function problemOf(value: unknown): Problem | null {
  if (value instanceof ApiError) return value.problem ?? null;
  if (looksLikeProblem(value)) return value;
  return null;
}

export function isPendingEndpoint(value: unknown): value is ApiError {
  return value instanceof ApiError && value.kind === 'pending-endpoint';
}

export function pendingEndpoint(capability: string, why: string): ApiError {
  return new ApiError(
    'pending-endpoint',
    `this client has no live call for “${capability}” yet: ${why}`,
    { capability },
  );
}

export function looksLikeProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) return false;
  const rec = value as Record<string, unknown>;
  return (
    typeof rec.type === 'string' &&
    typeof rec.title === 'string' &&
    typeof rec.status === 'number' &&
    typeof rec.detail === 'string' &&
    typeof rec.code === 'string'
  );
}

/** Status → code fallback for bodies that are *not* problem+json. */
export function synthesizeProblem(status: number, detail: string, code?: ProblemCode): Problem {
  return {
    type: 'about:blank',
    title: `HTTP ${status}`,
    status,
    detail,
    code: code ?? codeForStatus(status),
  };
}

function codeForStatus(status: number): ProblemCode {
  if (status === 401) return 'signature-invalid';
  if (status === 403) return 'scope-insufficient';
  if (status === 404) return 'device-not-found';
  if (status === 409) return 'idempotency-conflict';
  if (status === 422) return 'reference-invalid';
  if (status === 429) return 'rate-limited';
  if (status >= 500) return 'acs-unreachable';
  return 'acs-unreachable';
}

export function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds);
  const date = Date.parse(value);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.round((date - Date.now()) / 1000));
}
